import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ArrowLeft, Trophy } from "lucide-react";

const MEDALS = ["🥇", "🥈", "🥉"];

function LeaderboardRow({ a, i, total }) {
  return (
    <div className={`flex items-center justify-between border-b border-white/5 px-4 py-3 last:border-b-0 ${i < 3 ? "bg-amber-500/5" : ""}`} data-testid={`leaderboard-row-${i}`}>
      <div className="flex items-center gap-4">
        <div className="w-8 text-center font-mono-num text-lg">{MEDALS[i] || `${i + 1}.`}</div>
        <div>
          <div className="font-semibold text-white">{a.student_name}</div>
          <div className="text-xs text-slate-500">{new Date(a.created_at).toLocaleString()}</div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="h-1.5 w-28 overflow-hidden rounded-full bg-white/5">
          <div className="h-full bg-cyan-400" style={{ width: `${total ? (a.score / total) * 100 : 0}%` }} />
        </div>
        <div className="font-mono-num text-lg text-cyan-200">{a.score}<span className="text-slate-500">/{a.total}</span></div>
      </div>
    </div>
  );
}

function useLeaderboard(qid, enabled) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!enabled) return;
    api.get(`/teacher/quizzes/${qid}/attempts`).then((r) => setData(r.data)).catch((e) => setErr(apiError(e, "Could not load leaderboard.")));
  }, [qid, enabled]);
  return { data, err };
}

export default function Leaderboard() {
  const { qid } = useParams();
  const { user, loading } = useAuth();
  const isTeacher = user?.role === "teacher";
  const { data, err } = useLeaderboard(qid, isTeacher);

  if (loading) return <div className="text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isTeacher) return <Navigate to="/dashboard" replace />;
  if (err) return <div className="rounded-md border border-red-400/40 bg-red-500/10 p-6 text-red-200">{err}</div>;
  if (!data) return <div className="text-slate-400">Loading…</div>;

  const total = data.attempts[0]?.total || 0;
  return (
    <div className="space-y-8" data-testid="leaderboard-root">
      <div>
        <Link to="/teacher" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 hover:text-cyan-300"><ArrowLeft size={14} /> Back to my quizzes</Link>
        <h1 className="font-display mt-4 text-4xl font-bold tracking-tight sm:text-5xl">{data.quiz.title}</h1>
        <p className="mt-2 text-sm text-slate-400">Live leaderboard — updates as students submit.</p>
      </div>
      <div className="rounded-md border border-white/10 bg-black/40" data-testid="leaderboard-list">
        {data.attempts.length === 0 && (
          <div className="p-10 text-center text-sm text-slate-500"><Trophy className="mx-auto mb-3 text-slate-600" size={28} />No attempts yet. Share the link with your class.</div>
        )}
        {data.attempts.map((a, i) => <LeaderboardRow key={a.id || `${a.student_name}-${a.created_at}`} a={a} i={i} total={total} />)}
      </div>
    </div>
  );
}
